import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Radar, ArrowLeft, LayoutDashboard, UserCircle2, BookOpen, Search, TerminalSquare,
  MessagesSquare, Sparkles, Gauge, Award, CheckCircle2, Circle, Send, Loader2,
  GraduationCap, Lock, Printer, ShieldAlert, Eye,
} from "lucide-react";
import LiveInterview from "@/components/LiveInterview";

const ACCENT = "#38bdf8";

const NAV = [
  { id: "journey", label: "Journey", icon: LayoutDashboard },
  { id: "profile", label: "Your Profile", icon: UserCircle2 },
  { id: "learn", label: "Learn", icon: BookOpen },
  { id: "investigation", label: "Investigation", icon: Search },
  { id: "kql", label: "KQL Lab", icon: TerminalSquare },
  { id: "interview", label: "Mock Interview", icon: MessagesSquare },
  { id: "coach", label: "SOC Coach", icon: Sparkles },
  { id: "readiness", label: "Readiness", icon: Gauge },
  { id: "certificate", label: "Certificate", icon: Award },
];

const EXPERIENCE = ["New to cybersecurity", "IT / Helpdesk background", "Networking background", "Some SOC exposure",
  "SOC Tier 1", "SOC Tier 2", "SOC Tier 3 / Senior", "Detection Engineer", "Incident Responder"];
const ROLES = ["SOC Analyst Tier 1", "SOC Analyst Tier 2", "SOC Analyst Tier 3", "Security Analyst", "MDR Analyst",
  "Detection & Response Analyst", "Incident Response Analyst", "Threat Hunter", "Microsoft Security Analyst", "SIEM Analyst"];
const SIEMS = ["Microsoft Sentinel / Defender XDR", "Splunk Enterprise Security", "IBM QRadar", "Elastic Security",
  "Google Security Operations", "Palo Alto Cortex", "CrowdStrike Falcon"];
const COMPANIES = ["MSSP / MDR", "Enterprise in-house SOC", "Financial Services", "Healthcare", "Technology / SaaS",
  "Government / Defense", "Retail", "General Enterprise"];
const DIFFICULTY = ["Beginner", "Intermediate", "Advanced", "Senior / Expert", "High-Pressure"];

function renderMd(text) {
  return (text || "").split("\n").map((raw, i) => {
    const line = raw.replace(/\*\*(.+?)\*\*/g, "‹b›$1‹/b›");
    const parts = line.split(/(‹b›.+?‹\/b›)/g).map((p, j) =>
      p.startsWith("‹b›") ? <strong key={j}>{p.slice(3, -4)}</strong> : <span key={j}>{p}</span>);
    if (raw.startsWith("### ")) return <h4 key={i} className="font-semibold mt-3">{raw.slice(4)}</h4>;
    if (raw.startsWith("## ")) return <h3 key={i} className="font-semibold text-base mt-3">{raw.slice(3)}</h3>;
    if (raw.startsWith("# ")) return <h3 key={i} className="font-bold text-base mt-2">{raw.slice(2)}</h3>;
    if (/^[-*] /.test(raw)) return <li key={i} className="ml-4 list-disc">{parts}</li>;
    if (raw.trim() === "---" || raw.trim() === "") return <div key={i} className="h-2" />;
    return <p key={i} className="my-0.5">{parts}</p>;
  });
}

export default function SocLab() {
  const navigate = useNavigate();
  const [view, setView] = useState("journey");
  const [ov, setOv] = useState(null);
  const loadOv = () => api.get("/soc/overview").then(({ data }) => setOv(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { loadOv(); }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background text-foreground" style={{ ["--acc"]: ACCENT }} data-testid="soc-lab">
      <header className="h-14 shrink-0 flex items-center gap-3 px-4 border-b border-border" style={{ background: "linear-gradient(90deg,#0b1220,#0369a1)" }}>
        <button onClick={() => navigate("/interview-prep")} className="text-white/80 hover:text-white flex items-center gap-1.5 text-sm" data-testid="soc-back"><ArrowLeft className="h-4 w-4" /> Interview Prep</button>
        <span className="text-white/40">/</span>
        <div className="flex items-center gap-2 text-white font-semibold"><Radar className="h-5 w-5" /> SOC Analyst</div>
        <button onClick={() => navigate("/labs")} className="ml-auto text-white/80 hover:text-white text-sm" data-testid="soc-back-labs">HMG Labs</button>
        {ov && <span className="text-white text-xs bg-white/15 rounded-full px-2.5 py-1" data-testid="soc-progress">{ov.progress.done}/{ov.progress.total} steps</span>}
      </header>

      <div className="flex-1 flex overflow-hidden">
        <nav className="w-56 shrink-0 border-r border-border bg-card overflow-y-auto py-3">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setView(n.id)} data-testid={`soc-nav-${n.id}`}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm ${view === n.id ? "bg-[color:var(--acc)]/10 text-[color:var(--acc)] font-medium border-r-2 border-[color:var(--acc)]" : "text-muted-foreground hover:bg-accent"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <main className="flex-1 overflow-y-auto">
          {!ov ? <div className="h-full grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div> : (
            <>
              {view === "journey" && <Journey ov={ov} go={setView} />}
              {view === "profile" && <Profile ov={ov} reload={loadOv} />}
              {view === "learn" && <Learn reload={loadOv} />}
              {view === "investigation" && <Investigation reload={loadOv} />}
              {view === "kql" && <KqlLab reload={loadOv} />}
              {view === "interview" && <Interview ov={ov} reload={loadOv} />}
              {view === "coach" && <Coach />}
              {view === "readiness" && <Readiness />}
              {view === "certificate" && <Certificate />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function Section({ title, desc, children, testid }) {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid={testid}>
      <h2 className="text-xl font-bold">{title}</h2>
      {desc && <p className="text-sm text-muted-foreground mt-1">{desc}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}
function Block({ title, items, tone }) {
  const c = { emerald: "text-emerald-600", amber: "text-amber-600", red: "text-red-600", slate: "text-muted-foreground" }[tone];
  if (!items || !items.length) return null;
  return (
    <div className="mt-3 text-sm">
      <div className={`font-semibold ${c}`}>{title}</div>
      <ul className="mt-0.5 space-y-0.5">{items.map((it, i) => <li key={i} className="text-muted-foreground flex gap-1.5"><span className={c}>•</span>{it}</li>)}</ul>
    </div>
  );
}

function Journey({ ov, go }) {
  const pct = Math.round(100 * ov.progress.done / ov.progress.total);
  const map = { onboarding: "profile", learn: "learn", investigation: "investigation", kql: "kql" };
  return (
    <Section title="Welcome to the SOC Analyst track" desc="Learn → Investigate → Hunt → Interview → Graduate. Complete every step to earn your certificate." testid="soc-journey">
      <div className="rounded-xl border border-border bg-card p-5 flex items-center gap-4 mb-6">
        <ShieldAlert className="h-8 w-8 text-[color:var(--acc)]" />
        <div>
          <div className="font-semibold">NorthStar Financial Technologies · SOC (training environment)</div>
          <div className="text-xs text-muted-foreground mt-0.5">You're the analyst on shift. Work through realistic incidents, then talk through your decisions in interviews.</div>
        </div>
      </div>
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-1"><span className="font-medium">Overall progress</span><span className="text-muted-foreground">{pct}%</span></div>
        <div className="h-2.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: ACCENT }} /></div>
      </div>
      <div className="space-y-2">
        {ov.steps.map((s) => (
          <button key={s.id} onClick={() => go(map[s.id] || "interview")} data-testid={`soc-step-${s.id}`}
            className="w-full flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left hover:border-[color:var(--acc)] transition-colors">
            {s.done ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
            <span className={`text-sm ${s.done ? "text-muted-foreground line-through" : "font-medium"}`}>{s.label}</span>
          </button>
        ))}
      </div>
      {ov.graduated && (
        <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex items-center gap-3">
          <GraduationCap className="h-8 w-8 text-emerald-500" />
          <div className="flex-1"><div className="font-semibold text-emerald-700 dark:text-emerald-300">You've completed every step!</div>
            <div className="text-sm text-muted-foreground">Claim your SOC Analyst Certificate of Completion.</div></div>
          <button onClick={() => go("certificate")} className="text-sm font-medium text-emerald-600 hover:underline" data-testid="soc-goto-cert">View certificate →</button>
        </div>
      )}
    </Section>
  );
}

function Profile({ ov, reload }) {
  const p = ov.profile || {};
  const [f, setF] = useState({ experience: p.experience || "", target_role: p.target_role || "", siem: p.siem || "", company: p.company || "", difficulty: p.difficulty || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.experience || !f.target_role || !f.siem) return toast.error("Please choose your experience, target role and SIEM.");
    setBusy(true);
    try { await api.post("/soc/profile", f); toast.success("Profile saved. Your interviews will be tailored to this."); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const Group = ({ label, options, value, onPick, testid }) => (
    <div className="mb-6">
      <div className="text-sm font-medium mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} data-testid={`${testid}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} onClick={() => onPick(o)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value === o ? "text-white border-transparent" : "border-border hover:border-[color:var(--acc)]"}`}
            style={value === o ? { background: ACCENT } : {}}>{o}</button>
        ))}
      </div>
    </div>
  );
  return (
    <Section title="Your SOC analyst profile" desc="Interviews and coaching adapt to where you are and the stack you're targeting." testid="soc-profile">
      <Group label="Experience" options={EXPERIENCE} value={f.experience} onPick={(o) => setF({ ...f, experience: o })} testid="soc-exp" />
      <Group label="Target role / tier" options={ROLES} value={f.target_role} onPick={(o) => setF({ ...f, target_role: o })} testid="soc-role" />
      <Group label="Primary SIEM / platform" options={SIEMS} value={f.siem} onPick={(o) => setF({ ...f, siem: o })} testid="soc-siem" />
      <Group label="Target company type" options={COMPANIES} value={f.company} onPick={(o) => setF({ ...f, company: o })} testid="soc-company" />
      <Group label="Interview difficulty" options={DIFFICULTY} value={f.difficulty} onPick={(o) => setF({ ...f, difficulty: o })} testid="soc-diff" />
      <button onClick={save} disabled={busy} data-testid="soc-profile-save" className="px-5 py-2.5 rounded-lg text-white text-sm font-medium flex items-center gap-2" style={{ background: ACCENT }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save profile
      </button>
    </Section>
  );
}

function Learn({ reload }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/soc/lessons").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const complete = async (id) => {
    try { await api.post(`/soc/lessons/${id}/complete`); toast.success("Lesson complete"); await load(); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const done = data.done || [];
  return (
    <Section title="SOC Fundamentals" desc="Short, interview-focused lessons. Finish all of them to clear the Learn step." testid="soc-learn">
      <div className="space-y-3">
        {data.lessons.map((l) => {
          const isDone = done.includes(l.id);
          const isOpen = open === l.id;
          return (
            <div key={l.id} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`soc-lesson-${l.id}`}>
              <button onClick={() => setOpen(isOpen ? null : l.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                {isDone ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
                <div className="flex-1">
                  <div className="font-medium text-sm">{l.title}</div>
                  <div className="text-xs text-muted-foreground">{l.category} · {l.minutes} min · {l.summary}</div>
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 border-t border-border">
                  <ul className="space-y-2 my-3">
                    {l.key_points.map((k, i) => <li key={i} className="flex gap-2 text-sm"><span className="text-[color:var(--acc)] mt-0.5">▸</span><span>{k}</span></li>)}
                  </ul>
                  <button onClick={() => complete(l.id)} data-testid={`soc-lesson-complete-${l.id}`} disabled={isDone}
                    className="px-4 py-2 rounded-lg text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-50" style={{ background: ACCENT }}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> {isDone ? "Completed" : "Mark as complete"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Investigation({ reload }) {
  const [data, setData] = useState(null);
  const [opened, setOpened] = useState([]);
  const [answers, setAnswers] = useState({});
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/soc/investigation").then(({ data }) => { setData(data); if (data.state?.submitted) { setReport({ report: data.state.report }); setOpened(data.state.opened || []); setAnswers(data.state.answers || {}); } });
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const S = data.scenario;
  const openEv = (id) => setOpened((o) => o.includes(id) ? o : [...o, id]);
  const setAns = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const toggleMulti = (id, opt) => setAnswers((a) => {
    const cur = a[id] || [];
    return { ...a, [id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });
  const submit = async () => {
    const flat = {}; Object.entries(answers).forEach(([k, v]) => flat[k] = Array.isArray(v) ? v.join(", ") : v);
    setBusy(true);
    try { const { data } = await api.post("/soc/investigation/submit", { opened, answers: flat }); setReport(data); reload(); toast.success("Investigation submitted & graded"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const inp = "w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm";
  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="soc-investigation">
      <h2 className="text-xl font-bold">{S.title}</h2>
      <p className="text-sm text-muted-foreground mt-1">{S.org}</p>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {/* Left — case + questions */}
        <div className="space-y-4">
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
            <div className="font-semibold text-sm mb-1 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-red-500" /> Incident alert</div>
            <p className="text-sm">{S.alert}</p>
            <p className="text-xs text-muted-foreground mt-2">{S.intro}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 space-y-4">
            <div className="font-semibold text-sm">Complete the case</div>
            {S.questions.map((q) => (
              <div key={q.id} data-testid={`soc-q-${q.id}`}>
                <div className="text-sm font-medium mb-1.5">{q.q}</div>
                {q.type === "text" && <textarea rows={2} className={inp} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} data-testid={`soc-ans-${q.id}`} placeholder="Your analysis…" />}
                {q.type === "choice" && (
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((o) => <button key={o} onClick={() => setAns(q.id, o)} data-testid={`soc-ans-${q.id}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs border ${answers[q.id] === o ? "text-white border-transparent" : "border-border hover:border-[color:var(--acc)]"}`} style={answers[q.id] === o ? { background: ACCENT } : {}}>{o}</button>)}
                  </div>
                )}
                {q.type === "multi" && (
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((o) => { const on = (answers[q.id] || []).includes(o); return <button key={o} onClick={() => toggleMulti(q.id, o)}
                      className={`px-3 py-1.5 rounded-full text-xs border ${on ? "text-white border-transparent" : "border-border hover:border-[color:var(--acc)]"}`} style={on ? { background: ACCENT } : {}}>{o}</button>; })}
                  </div>
                )}
              </div>
            ))}
            {!data.state?.submitted && <button onClick={submit} disabled={busy} data-testid="soc-inv-submit" className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1.5" style={{ background: ACCENT }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Submit investigation</button>}
          </div>
          {report && <InvReport report={report.report} />}
        </div>
        {/* Right — evidence reveal */}
        <div className="space-y-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Eye className="h-4 w-4" /> Evidence ({opened.length}/{S.evidence.length} opened)</div>
          <p className="text-xs text-muted-foreground">Open only what you need — methodology is scored.</p>
          {S.evidence.map((e) => {
            const isOpen = opened.includes(e.id);
            return (
              <div key={e.id} className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`soc-ev-${e.id}`}>
                <button onClick={() => openEv(e.id)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-accent" data-testid={`soc-ev-open-${e.id}`}>
                  {isOpen ? <CheckCircle2 className="h-4 w-4 text-[color:var(--acc)]" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  <span className="font-medium">{e.label}</span>
                  {!isOpen && <span className="ml-auto text-xs text-muted-foreground">Open</span>}
                </button>
                {isOpen && <div className="px-3 pb-3 text-xs text-muted-foreground border-t border-border pt-2 font-mono leading-relaxed">{e.body}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
function InvReport({ report }) {
  const c = report.overall >= 70 ? "#10b981" : report.overall >= 50 ? ACCENT : "#f59e0b";
  return (
    <div className="rounded-xl border-2 p-5" style={{ borderColor: c }} data-testid="soc-inv-report">
      <div className="flex items-center gap-3">
        <div className="text-3xl font-bold" style={{ color: c }}>{report.overall}<span className="text-base text-muted-foreground">/100</span></div>
        <div className="text-sm font-semibold">Investigation graded</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center text-xs">
        {[["Technical", report.technical_accuracy], ["Methodology", report.investigation_methodology], ["Evidence", report.evidence_awareness]].map(([k, v]) => (
          <div key={k} className="rounded-lg bg-muted py-2"><div className="font-semibold text-sm">{v}</div><div className="text-muted-foreground">{k}</div></div>
        ))}
      </div>
      <Block title="Strengths" items={report.strengths} tone="emerald" />
      <Block title="Gaps" items={report.gaps} tone="amber" />
      {report.correct_summary && <div className="mt-3 text-sm"><div className="font-semibold">What actually happened</div><p className="text-muted-foreground mt-0.5">{report.correct_summary}</p></div>}
    </div>
  );
}

function KqlLab({ reload }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/soc/kql").then(({ data }) => { setData(data); if (data.state?.submitted) { setReport(data.state.report); setQ(data.state.query || ""); } }); }, []);
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const L = data.lab;
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/soc/kql/submit", { query: q }); setReport(data.report); reload(); toast.success("Query graded"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Section title={L.title} desc="Write a KQL hunting query against sample Microsoft log data. Your logic matters more than getting every field name exact." testid="soc-kql">
      <div className="rounded-xl border border-border bg-card p-4 mb-4">
        <div className="font-semibold text-sm mb-1">Task</div>
        <p className="text-sm">{L.task}</p>
        <div className="font-semibold text-sm mt-3 mb-1">Schema</div>
        <p className="text-xs font-mono text-muted-foreground">{L.schema[0]}</p>
        <div className="text-xs text-muted-foreground mt-2"><span className="font-semibold">Hint:</span> {L.hint}</div>
      </div>
      <textarea value={q} onChange={(e) => setQ(e.target.value)} rows={7} spellCheck={false} data-testid="soc-kql-input"
        className="w-full rounded-lg border border-input bg-[#0b1220] text-sky-100 font-mono text-sm px-3 py-2.5 leading-relaxed"
        placeholder={"SigninLogs\n| where TimeGenerated > ago(24h)\n| ..."} />
      <button onClick={submit} disabled={busy} data-testid="soc-kql-submit" className="mt-3 px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1.5" style={{ background: ACCENT }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <TerminalSquare className="h-4 w-4" />} Run & grade my query
      </button>
      {report && (
        <div className="rounded-xl border-2 p-5 mt-4" style={{ borderColor: report.overall >= 70 ? "#10b981" : report.overall >= 50 ? ACCENT : "#f59e0b" }} data-testid="soc-kql-report">
          <div className="flex items-center gap-4 text-center text-xs">
            <div className="text-3xl font-bold" style={{ color: report.overall >= 50 ? ACCENT : "#f59e0b" }}>{report.overall}<span className="text-base text-muted-foreground">/100</span></div>
            {[["Syntax", report.syntax], ["Logic", report.logic], ["Efficiency", report.efficiency]].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-muted py-2 px-3"><div className="font-semibold text-sm">{v}</div><div className="text-muted-foreground">{k}</div></div>
            ))}
          </div>
          <Block title="Strengths" items={report.strengths} tone="emerald" />
          <Block title="Gaps" items={report.gaps} tone="amber" />
          {report.improved_query && <div className="mt-3"><div className="font-semibold text-sm">Ideal query</div><pre className="mt-1 rounded-lg bg-[#0b1220] text-sky-100 font-mono text-xs p-3 whitespace-pre-wrap">{report.improved_query}</pre></div>}
        </div>
      )}
    </Section>
  );
}

function Interview({ ov, reload }) {
  const modes = [
    { id: "hiring_manager", label: "Hiring Manager", desc: "Ownership, prioritization, escalation & communication.", best: ov.interviews.hiring_manager.best_score },
    { id: "tier1", label: "SOC Tier 1", desc: "Alert triage, TP/FP/BP, IOCs, identity & phishing.", best: ov.interviews.tier1.best_score },
    { id: "tier2", label: "SOC Tier 2", desc: "Scoping, timelines, containment, KQL & MITRE (optional).", best: ov.interviews.tier2.best_score },
  ];
  return <LiveInterview apiBase="soc" accent={ACCENT} testidPrefix="soc" onGraded={reload} modes={modes} />;
}
function Coach() {
  const [msgs, setMsgs] = useState([{ role: "coach", text: "Hi, I'm your SOC Coach. Ask me anything, like 'How do I investigate impossible travel?', 'Write a KQL query for password spraying', or 'What's the difference between an alert and an incident?'" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const ask = async (text) => {
    const t = (text || input).trim(); if (!t || busy) return;
    setMsgs((m) => [...m, { role: "you", text: t }]); setInput(""); setBusy(true);
    try { const { data } = await api.post("/soc/coach", { text: t }); setMsgs((m) => [...m, { role: "coach", text: data.answer }]); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl mx-auto px-6 py-8 h-full flex flex-col" data-testid="soc-coach">
      <h2 className="text-xl font-bold mb-4">Ask SOC Coach</h2>
      <div className="flex-1 overflow-y-auto rounded-xl border border-border bg-card p-4 space-y-3">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "you" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`soc-coach-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "you" ? "text-white" : "bg-muted"} [&_li]:my-0.5`} style={{ ...(m.role === "you" ? { background: ACCENT } : {}), ["--acc"]: ACCENT }}>
              {m.role === "you" ? m.text : <div>{renderMd(m.text)}</div>}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-muted rounded-2xl px-4 py-2.5"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex items-end gap-2 mt-3">
        <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} rows={2} placeholder="Ask the coach…" data-testid="soc-coach-input" className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none" />
        <button onClick={() => ask()} disabled={busy} data-testid="soc-coach-send" className="h-10 w-10 shrink-0 rounded-lg text-white grid place-items-center" style={{ background: ACCENT }}><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function Readiness() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/soc/readiness").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const bar = (v) => v >= 80 ? "#10b981" : v >= 60 ? ACCENT : v >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <Section title="SOC Interview Readiness" desc="Your readiness across SOC competencies, from lessons, your investigation, KQL lab and interview scores." testid="soc-readiness">
      <div className="rounded-xl border border-border bg-card p-6 mb-6 text-center">
        <div className="text-5xl font-bold" style={{ color: bar(data.overall) }}>{data.overall}%</div>
        <div className="text-sm text-muted-foreground mt-1">Overall readiness</div>
      </div>
      <div className="space-y-3">
        {Object.entries(data.categories).map(([k, v]) => (
          <div key={k} data-testid={`soc-readiness-${k.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
            <div className="flex justify-between text-sm mb-1"><span>{k}</span><span className="text-muted-foreground">{v}%</span></div>
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: bar(v) }} /></div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Certificate() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/soc/certificate").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!data.eligible) {
    return (
      <Section title="Certificate of Completion" testid="soc-certificate">
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <Lock className="h-10 w-10 text-muted-foreground mx-auto" />
          <div className="font-semibold mt-3">Not unlocked yet</div>
          <p className="text-sm text-muted-foreground mt-1">Complete every journey step to unlock your certificate:</p>
          <ul className="text-sm text-left max-w-sm mx-auto mt-3 space-y-1">{data.remaining.map((r, i) => <li key={i} className="flex gap-2"><Circle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />{r}</li>)}</ul>
        </div>
      </Section>
    );
  }
  const c = data.certificate;
  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="soc-certificate">
      <div className="flex justify-end mb-4 print:hidden">
        <button onClick={() => window.print()} data-testid="soc-cert-print" className="text-sm px-4 py-2 rounded-lg text-white font-medium flex items-center gap-1.5" style={{ background: ACCENT }}><Printer className="h-4 w-4" /> Print / Save PDF</button>
      </div>
      <div className="rounded-2xl border-4 p-10 text-center bg-card" style={{ borderColor: ACCENT }}>
        <Radar className="h-14 w-14 mx-auto" style={{ color: ACCENT }} />
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-4">HMG Global Consults · Interview Prep Lab</div>
        <h1 className="text-2xl font-bold mt-3">{data.title}</h1>
        <p className="text-sm text-muted-foreground mt-6">This certifies that</p>
        <div className="text-3xl font-bold my-2" style={{ color: ACCENT }}>{c.name}</div>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto">has completed the hands-on SOC Analyst interview-preparation lab, investigating a realistic account-compromise incident, hunting with KQL, and clearing the hiring-manager and Tier 1 SOC interviews.</p>
        <div className="text-lg font-semibold mt-6" style={{ color: ACCENT }}>🎉 {data.message}</div>
        <div className="flex flex-wrap justify-center gap-1.5 mt-6">{data.skills.map((s) => <span key={s} className="text-[11px] px-2.5 py-1 rounded-full bg-muted">{s}</span>)}</div>
        <div className="flex justify-between items-end mt-8 text-xs text-muted-foreground border-t border-border pt-4">
          <div className="text-left">Certificate ID<br /><span className="font-mono text-foreground">{c.id}</span></div>
          <div className="text-right">Issued<br /><span className="text-foreground">{c.issued}</span></div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-4 italic">{data.disclaimer}</p>
      </div>
    </div>
  );
}
